import {
  Component,
  ChangeDetectionStrategy,
  ElementRef,
  ChangeDetectorRef,
  ComponentFactoryResolver,
  ApplicationRef,
  Injector,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ViewContainerRef,
  ComponentRef,
} from '@angular/core';
import { WizDateUtils } from '@wizeoo/akm-core-lib';
import { WizComponentManager, WizRoot, WizVgroup, WizEventManager, WizTranslationManager } from '@wizeoo/akm-component-lib';
import { CalendarOptions, Calendar } from '@fullcalendar/core'; // l'import calendar est nécessaire, sinon ça plante
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import allLocales from '@fullcalendar/core/locales-all';
import { ComponentPortal, DomPortalOutlet } from '@angular/cdk/portal';
import { WidgetConfigurationItemObj} from '@wizeoo/akm-message-lib';

import { ALK_EVENT_TYPE, WIDGET_ACTION_ORIGIN, WIDGET_GLOBAL_KIND, WIDGET_KIND, WIDGET_STATE } from '../../../model/enums';
import { ViewData } from '../../../model/view.data';
import { FreedataArgsVO } from '../../../vo/core/freedataargs.vo';
import { FreedataValueVO } from  '../../../vo/core/freedatavalue.vo';
import { IWidget, IWidgetFilter } from '../../../manager/widgets/i-widget';
import { FreeDataVO } from '../../../vo/core/freedata.vo';
import { FastSelectData } from './../../../manager/widgets/widget.engine';
import { AlkEvent } from '../../../framework/events/alk.event';
import { FreedataWidgetBasic } from '../base/freedata-widget-basic';
import { Color, colorSets } from '../../ngx-charts/utils/color-sets';
import { PivotField } from '../../../manager/baselines/pivot-field.obj';
import { PivotFilterExtendedRenderer } from '../pivot/renderers/filters/pivot-filter-extended.renderer';
import { FreedataTimelineSettingsPop } from './pops/freedata-timeline-settings.pop';
import { TimelineFieldRenderer } from '../timeline/filters/timeline-field.renderer';
import { TimelineEventRenderer } from '../timeline/timeline-event.renderer';
import { timelineEntry } from '../timeline/timeline.widget';
import { FIELD_KIND } from '@wizeoo/akm-compute-lib';

@Component({
  selector: 'freedata-timeline-widget',
  templateUrl: './freedata-timeline.widget.html',
  styleUrls: ['./freedata-timeline.widget.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/** affichage des données en mode calendaire */
export class FreedataTimelineWidget extends FreedataWidgetBasic implements AfterViewInit, OnDestroy {
  
  constructor( elementRef: ElementRef, private cdref: ChangeDetectorRef, protected componentFactoryResolver: ComponentFactoryResolver,
    protected appRef: ApplicationRef, protected injector: Injector
  ) {
    super(elementRef);
    this.widgetKind = WIDGET_KIND.FD_TIMELINE;
    this.widgetGlobalKind = WIDGET_GLOBAL_KIND.FREEDATA;
    this.settingsPopClassFactory = FreedataTimelineSettingsPop;
  }
  
  /* ##############################################################################
                  composants spécifiques
  ############################################################################## */

  @ViewChild('vgDetailZone', { read: WizVgroup, static: true }) vgDetailZone: WizVgroup; // zone de filtre, pour écouteur sur setFilter
  @ViewChild('containerFilter', { read: ViewContainerRef, static: true }) containerFilter: ViewContainerRef; // zone d'ajout des champs de filtre
  @ViewChild('containerFields', { read: ViewContainerRef, static: true }) containerFields: ViewContainerRef; // zone d'ajout des champs de filtre
  @ViewChild('fullcalendar') fullcalendar: any; // zone d'ajout des champs de filtre

  public filterComponents: ComponentRef<PivotFilterExtendedRenderer>[]; // les champs de filtre
  public fieldsComponents: ComponentRef<TimelineFieldRenderer>[]; // les champs de sélection de dates
  public readyToGo = false; // le calendar est OK, sinon sale effet de refresh

  public tooMany: boolean = false; // trop d'élément pour l'affichage
  public tooManyLabel: string = '';

  /* ##############################################################################
                  paramètres spécifiques
  ############################################################################## */

  public resources = []; // la collection de ressources (les lignes)
  public events = []; // la collection d'events
  public scheme: Color = colorSets[ViewData.DEFAULT_COLOR_SET]; // jeu de couleur

  /**
   * injection du contenu Angular sur une entrée html
   * @param info les infos du renderer de base (html)
   */
  private doMount(info) {
    // console.log('info', info);
    const bodyPortalHost = new DomPortalOutlet(
      info.el, // Target the element where you wish to append your component
      this.componentFactoryResolver,
      this.appRef,
      this.injector
    );

    const newCompo = new ComponentPortal(TimelineEventRenderer); // renderer à injecter
    const refCompo = bodyPortalHost.attach(newCompo);
    refCompo.instance.data = info.event._def; // passage du data
  }

  calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, interactionPlugin, resourceTimelinePlugin ],
    editable: false,
    firstDay: 1,
    events: this.events,

    resources: this.resources,
    resourceAreaColumns: [
      {
        field: 'title',
        headerContent: ' '
      },
    ],

    eventDidMount: this.doMount.bind(this), // injection de composant dans les renderers
    fixedMirrorParent: document.body,
    resourceAreaWidth:'200px',
    initialView: 'meYear',
    headerToolbar: {
      left: 'prev,next,today',
      center: 'title',
      right: 'meYear,meTrim,meMonth',
    },
    eventClick: this.handleEventClick.bind(this),
    datesSet: this.changeDate.bind(this),
    locales: allLocales, 
    locale: 'fr',
    aspectRatio: 2,
    views: {
      meYear: {
        type: 'resourceTimeline',
        duration: { years: 1 },
        buttonText: WizTranslationManager.getInstance().translateKey('$.common.year.$'),
        slotDuration: { weeks: 1 },
        slotLabelFormat: [
          { week: 'numeric' } // lower level of text
        ]

      },
      meTrim: {
        type: 'resourceTimeline',
        duration: { months: 3 },
        buttonText: WizTranslationManager.getInstance().translateKey('$.common.quarter.$'),
        slotDuration: { weeks: 1 }  ,
        slotLabelFormat: [
          { week: 'numeric' } // lower level of text
        ]   
      },
      meMonth: {
        type: 'resourceTimeline',
        duration: { months: 1 },
        buttonText: WizTranslationManager.getInstance().translateKey('$.common.month.$'),
        slotDuration: { weeks: 1 }  ,
        slotLabelFormat: [
          { week: 'numeric' } // lower level of text
        ]   
      }
    },
    
  };

   /* ##############################################################################
                    alimentation des données FEI / construction du grid
  ############################################################################### */
  /**
   * actions jouées sur le drawContent, le alterContent, ou en cas de modifs de filtre, de sélection
   * @override
   * @param origin WIDGET_ACTION_ORIGIN pour cibler qui demande
   * @param data l'objet éventuellement porté
   */
  public drawOrRefresh(origin: WIDGET_ACTION_ORIGIN, data?: any): void {
    let mustBuildRows: boolean = false; // on doit construire les lignes
    let mustRefresh: boolean = false; // création mise à jour de la DS de la liste
    let mustBuildFilters: boolean = false; // on doit construire les filtres
    let mustSelect: boolean = false; // on doit jouer la sélection de lignes

    // première création
    if(origin === WIDGET_ACTION_ORIGIN.INIT) {
      // alimentation du level pour filtre rapide des slaves
      this.freedataCode = this.context.fdk.split(ViewData.CONTEXT_SEPARATOR)[0];
      this.level = Number(this.context.fdk.split(ViewData.CONTEXT_SEPARATOR)[1]);
      
      // construction de la FeiKeyDef unique du widget
      this.buildFeiKeyDef(this.vision, this.context);
    }
    
    if(origin === WIDGET_ACTION_ORIGIN.READY) { // je me suis dit tout seul que j'étais prêt
      mustBuildFilters = true;
      mustBuildRows = true;
      mustSelect = true;
      mustRefresh = true;
    }

    // TODO sur configuration, capter les delta filter, colonnes, fields...

    // TODO TODO détecter une modification structurelle en cas 
    // contextDamaged
    // origine: changement de config
    // et mode pivot, sinon les lignes sont toujours OK
    if(origin === WIDGET_ACTION_ORIGIN.CONFIGURE) {   
      mustBuildFilters = true;
      mustBuildRows = true;
      mustRefresh = true;
    } 

    // modification du filtre... on filtre, et on corrige éventuellement la sélection
    // en cas de champ utilisé en colonne, on reconstruit le grid
    if(origin === WIDGET_ACTION_ORIGIN.FILTERING) {
      mustRefresh = true;
    }

    // modification du pool de sélection sur un master
    if(origin === WIDGET_ACTION_ORIGIN.OUTER_SELECT) {
      // console.log('outer select', this.widgetKind, this.fei.uniqueName, this.state);
      mustBuildRows = true;
      mustSelect = true;
      this.fdvDS.refresh(); // on applique le filtre
      mustRefresh = true;
    }

    if(origin === WIDGET_ACTION_ORIGIN.RECOMPUTE) { // TODO TODO Faire moins bourrin
      mustBuildRows = true;
      mustRefresh = true;
    }
    if(mustBuildFilters) {
      this.buildFilters();
    }
    if(mustBuildRows) {
      this.buildLines(); // délégué au WidgetBasic
    }

    if(mustSelect) {
      this.feedSelectionFromContext(this._context); // alimentation de la sélection et passage à WIDGET_STATE = SELECTION_COMPLETE
    }

    if(mustRefresh) {
      this.fdvDS.refresh();
      this.buildCalendar();
    }
  }

  /**
   * construction 'initiale' des objets graphiques du widget :
   * - sur init véritable
   * - sur modification de la datasource
   * - sur modification de scenario (modifie la DS)
   */
  public drawContent(): void {
    
    let master: IWidget = this.model.widgetEngine.checkFirstMaster(this); // je cherche un master. S'il y en a un, READY n'est pas suffisant
    // si j'ai un master et qu'il n'est pas prêt, on ne fait rien
    if (!!master && master.state !== WIDGET_STATE.SELECTION_COMPLETE) { // j'ai un master, mais il n'est pas prêt
      // console.log('checkFeiState master not ready', this.widgetKind, this.fei.uniqueName);
      return;
    }

    if(this.state >= WIDGET_STATE.READY) { // déjà construit, plus rien a faire
      return;
    }

    this.drawOrRefresh(WIDGET_ACTION_ORIGIN.INIT); // actions communes draw / alter
    super.drawContent(); // pas au début, c'est lui qui dispatch les events
  }

  /**
   * reconstruction des objets graphiques du widget sur changement de structure : 
   * - modification des filtres qui impactent les colonnes
   * - changement de champs en ligne / colonnes / data
   * - changement de filtre
   * - changement de sélection
   * @param origin 
   */
  public alterContent(origin: WIDGET_ACTION_ORIGIN): void {
    this.drawOrRefresh(origin);
    super.alterContent(origin);
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    WizEventManager.addListener(this, this.vgDetailZone, ALK_EVENT_TYPE.TIMELINE_SELECT_FIELD, this.refreshFromFields);
   }
 
   ngOnDestroy() {
     super.ngOnDestroy();
     WizEventManager.removeListener(this, this.vgDetailZone, ALK_EVENT_TYPE.TIMELINE_SELECT_FIELD);
   }

  /* ##############################################################################
                    gestion des champs à afficher
     ############################################################################## */

  /**
   * afficher ou pas des dates
   * @param e AlkEvent de type TIMELINE_SELECT_FIELD 
   */
  private refreshFromFields(e: AlkEvent): void {
    if (!this.templatingMode) {
      // persistence uniquement hors du template, sinon, c'est délégué
      this._saveContext();
    } else {
      this.settings = this.context;
      WizEventManager.dispatchEvent(this, new AlkEvent(ALK_EVENT_TYPE.WIDGET_SET_SETTINGS, this));
    }
    this.drawOrRefresh(WIDGET_ACTION_ORIGIN.FILTERING, e.data);
  }

  /** suppression des combos de filtres (avant changement de step, ou sur retour de pop) */
  private clearFieldsChecks(): void {
    this.containerFields.clear(); // vider le container des favoris
    this.fieldsComponents = []; // on rince la référence aux cpx
  }

  private buildFieldsChecks(): void {
    this.clearFieldsChecks();

    const fd: FreeDataVO = this.model.freedataEngine.getFreeData(this.freedataCode);


    if (this.context.c) {
      // collecte de la collection d'éléments à restituer
      let idx: number = 0;
      this.context.c.forEach((wcio: WidgetConfigurationItemObj) => {
        
        if(idx%2 === 0) { // on en prends 1/2, ce sont des paires de dates, pas besoin de la fin pour construire
        // collecte du arg correspondant pour alimenter correctement le label
        let argVO: FreedataArgsVO = this.model.freedataEngine.getArgFromFD(fd?.code, wcio.fdvf);

        // console.log('fdTimeline item', fd,  wcio);
        // if (!!this.fei.getKey(wcio.cod)) {
          const meCompo: ComponentRef<TimelineFieldRenderer> = WizComponentManager.instance.addChildTo(
            TimelineFieldRenderer,
            this.containerFields,
            { instanceProperties: { data: new PivotField(this, wcio, null, argVO) } }
          );
          this.fieldsComponents.push(meCompo);
        }
        idx++;

      });
    }
  }

  /* ##############################################################################
                    gestion des filtres (composants drop)
     ############################################################################## */

  /**
   * interception de modification de filtre pour déclencher un refresh
   */
  protected innerUpdateFilter(e: AlkEvent): void {
    super.innerUpdateFilter(e);
    this.drawOrRefresh(WIDGET_ACTION_ORIGIN.FILTERING, e.data);
  }

  /** suppression des combos de filtres (avant changement de step, ou sur retour de pop) */
  private clearFilterDrops(): void {
    this.containerFilter.clear(); // vider le container des favoris
    this.filterComponents = []; // on rince la référence aux filtres
  }

  /** construction des dropdowns de filtre selon le contexte */
  protected buildFilterDrops() {
    this.clearFilterDrops(); // on rince tout
    
    // construire unique le rendu, pas besoin d'alimenter les collections tant que la drop n'est pas ouverte
    if(this.filters) {
      [...this.filters.values()].forEach((filter: IWidgetFilter) => {
        const meCompo: ComponentRef<any> = WizComponentManager.instance.addChildTo(
          PivotFilterExtendedRenderer,
          this.containerFilter,
          { instanceProperties: { data: filter } }
        );
        this.filterComponents.push(meCompo);
      });
    }
    this.cdref.markForCheck();
  }

  /* ##############################################################################
             construction de la frise et des éléments
     ############################################################################## */

  /** construction graphique */
  private buildCalendar(): void {
    this.calendarOptions.locale = this.model.currentLocaleCode;
    
    this.calendarOptions.initialDate = this.getContextDate();
    this.calendarOptions.initialView = this.getContextMode();
    
    // construction des checkBoxes pour les champs en donnée
    this.buildFieldsChecks();

    // reconstruction des zones de filtre
    this.buildFilterDrops();

    // construction du calendar
    this.drawCalendar();
  }

  /**
   * reconstruction de la zone du calendar, sur init, changement de FEI, et modif de filtre
   */
  private drawCalendar():void {
    this.generateResources();
    this.generateEvents();

    // console.log('timeline drawContent', this.events);
    setTimeout(() => {
      this.calendarOptions.events = this.events;
      this.calendarOptions.resources = this.resources;
      this.readyToGo = true;
      this.cdref.markForCheck();
    }, 0); 
  }

  /** construction des entrées de calendrier */
  private generateResources(): void {
    this.resources = [];

    if(this.fdvDS.getWorkedSource().length > 500) {
      let nb: string = this.fdvDS.getWorkedSource().length.toString();
      let cleanMess: string = WizTranslationManager.getInstance().translateKey('$.timeline.toomany.$',[nb]);
      // WizPopUp.toast(cleanMess, WizIcon.TOAST_WARNING).show();
      this.tooMany = true;
      this.tooManyLabel = cleanMess;
      return;
    } else {
      this.tooMany = false;
    }

    // console.log('generateResources', this.mainDS.getWorkedSource().length);
    this.fdvDS.getWorkedSource().forEach((fdv: FreedataValueVO) => { 
      const titleParts: string[] = [];
      this.context.r.forEach((wcio: WidgetConfigurationItemObj) => {    
        let part: string = fdv.argsObjVO[wcio.fdvf];
        const conf: FreedataArgsVO = this.model.freedataEngine.getArgFromFD(fdv.freeDataCode, wcio.fdvf);
        if (!!conf) {
          part = this.model.formatEngine.applyFormat(part, conf.dataType, conf.format, null);
        }
        titleParts.push(part);
      });

      this.resources.push({
        id: fdv.id,
        title: titleParts.join(' - '),
      });
    });
  }

  /** construction des events calendar depuis mainDS */
  private generateEvents(): void {
    this.events = [];

    if(this.fdvDS.getWorkedSource().length > 500) {
      let nb: string = this.fdvDS.getWorkedSource().length.toString();
      let cleanMess: string = WizTranslationManager.getInstance().translateKey('$.timeline.toomany.$',[nb]);
      this.tooMany = true;
      this.tooManyLabel = cleanMess;
      return;
    } else {
      this.tooMany = false;
    }

    const domLen: number = this.scheme.domain.length; // prise du pool de couleurs
    const freedataCode: string = this.context.fdk.split(ViewData.CONTEXT_SEPARATOR)[0];
    const fd: FreeDataVO = this.model.freedataEngine.getFreeData(freedataCode);

    // création des paires de colonnes
    let entries: timelineEntry[] = [];

    for(let i: number = 0; i < this.context.c.length; i++) {
      const start: WidgetConfigurationItemObj = this.context.c[i]; // attention, D sur base, C ici...
      let end: WidgetConfigurationItemObj;

      if(i< this.context.c.length - 1) { // y'a quelqu'un derrière, c'est bien une paire
        i++;
      }
      end = this.context.c[i];


      if(!start.ro) { // recyclage du wcio.ro : si true, pas visible
        let contentLabel: string = start.lbl ? start.lbl : this.model.freedataEngine.getArgFromFD(fd.code, start.fdvf)?.trsLabel; 
        let backgroundColor: string = this.scheme.domain[start.ord % domLen];
        // console.log('gen', backgroundColor, i, contentLabel);
        let textColor: string = '#000000';
        // let evtColor: string = null;
        if (!!start.hsty) {
          const cleanStr = start.hsty.replace(new RegExp(ViewData.CONTEXT_SEPARATOR, 'g'), ''); // (ViewData.CONTEXT_SEPARATOR, '');
          backgroundColor = cleanStr.split(ViewData.KEY_SEPARATOR)[0];
          textColor = cleanStr.split(ViewData.KEY_SEPARATOR)[1];  
          if (!backgroundColor) {
            backgroundColor = this.scheme.domain[start.ord % domLen];
          }
        }
  
        let tle: timelineEntry = {startField: start.fdvf, endField: end.fdvf, label: contentLabel, backgroundColor, textColor };
        let argStart: FreedataArgsVO = this.model.freedataEngine.getArgFromFD(fd.code, start.fdvf);
        if(argStart?.dataType === FIELD_KIND.WORKFLOW) {
          tle.kindStart = FIELD_KIND.WORKFLOW;
          tle.settingsStart = start.pmdexParams;
        }
        let argEnd: FreedataArgsVO = this.model.freedataEngine.getArgFromFD(fd.code, end.fdvf);
        if(argEnd?.dataType === FIELD_KIND.WORKFLOW) {
          tle.kindEnd = FIELD_KIND.WORKFLOW;
          tle.settingsEnd = end.pmdexParams;
        }
        entries.push(tle);

      }
    }

    if(entries.length <= 0) { // vide, pas de datas
      return;
    }

    this.fdvDS.getWorkedSource().forEach((fdv: FreedataValueVO) => { 
      entries.forEach((tle: timelineEntry) => {
          
        let start: string = null;
        if(!!tle.startField) {
          if(tle.kindStart === FIELD_KIND.WORKFLOW) {
            let settings: any = {"item":"last","fields":["startDate"]};
            if(tle.settingsStart) {
              settings = JSON.parse(tle.settingsStart);
            }
            let strRes: string = this.model.workflowEngine.formatWorkflow(settings, fdv.argsObjVO[tle.startField]);
            if(strRes) {
              let objRes: any = JSON.parse(strRes);
              start = objRes.startDate;
            }
            
          } else {
            start = fdv.argsObjVO[tle.startField];
          }
        }

        
        let end: string = null;
        if(!!tle.endField) {
          if(tle.kindEnd === FIELD_KIND.WORKFLOW) {
            let settings: any = {"item":"last","fields":["endDate"]};
            if(tle.settingsEnd) {
              settings = JSON.parse(tle.settingsEnd);
            }
            let strRes: string = this.model.workflowEngine.formatWorkflow(settings, fdv.argsObjVO[tle.endField]);
            if(strRes) {
              let objRes: any = JSON.parse(strRes);
              end = objRes.endDate;
            }
          } else {
            end = fdv.argsObjVO[tle.endField];
          }
        }
          
        
        // console.log('fdv timelineEntry', fdv.code, tle.startField, tle.endField, start, end, tle.kindStart, tle.kindEnd);

        let datePart: string[] = [];
        datePart.push(WizDateUtils.numberToLocalizedString(Number(start?.substring(0,8))));
        datePart.push(WizDateUtils.numberToLocalizedString(Number(end?.substring(0,8))));
  
 
        let eventId: string = [fdv.id, tle.startField].join(ViewData.CONTEXT_SEPARATOR);
        this.events.push({
            id: eventId, 
            resourceId: fdv.id,
            editable: false, // this.checkMe(fdv),
            title: tle.label,
            start: WizDateUtils.dateToString(WizDateUtils.stringToTime(start), 'yyyy-MM-dd'),
            end: WizDateUtils.dateToString(WizDateUtils.stringToTime(end), 'yyyy-MM-dd'),
            textColor: tle.textColor,
            backgroundColor: tle.backgroundColor,
            borderColor: '#00000000',
            extendedProps: {
              context: this.context,
              title: tle.label,
              content: datePart.join(' - '),
              color: tle.textColor,
              freedataValue: null, 
              id: eventId,
              ds: this.mainDS,
            },
          });
      });
    });

  }

  /** sélection d'un event */
  handleEventClick(arg) {
    // console.log(arg);
    const fdv: FreedataValueVO = arg.event._def.extendedProps.freedataValue;
    this.fdvDS.toggle(fdv);
    let fsd: FastSelectData = {fei: this.fei, kind: this.widgetGlobalKind, data: fdv};
    WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.PIVOT_DOUBLE, fsd));
  }



}
